import { useEffect, useState } from 'react';
import { Plus, Folder as FolderIcon, X } from 'lucide-react';
import * as dashboardApi from '../api/dashboard-api';
import { TextInputDialog } from './TextInputDialog';
import type { Folder } from '@/contracts/funnel';

export interface FolderNavProps {
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
}

/**
 * SPEC-003 §22-27/T041: flat folder list — create/rename/delete/select.
 * Deleting a folder never deletes its funnels (DASH-REQ-021), only the
 * server enforces that; this UI just triggers the action.
 */
export function FolderNav({ selectedFolderId, onSelect }: FolderNavProps) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [renaming, setRenaming] = useState<Folder | null>(null);

  useEffect(() => {
    dashboardApi.listFolders().then((res) => setFolders(res.items));
  }, []);

  async function reload() {
    const res = await dashboardApi.listFolders();
    setFolders(res.items);
  }

  async function handleDelete(folder: Folder) {
    await dashboardApi.deleteFolder(folder.id);
    if (selectedFolderId === folder.id) onSelect(null);
    await reload();
  }

  return (
    <nav aria-label="Pastas" className="flex flex-col gap-1">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">Pastas</span>
        <button
          type="button"
          aria-label="Criar pasta"
          onClick={() => setCreateOpen(true)}
          className="rounded p-0.5 text-text-muted hover:text-accent"
        >
          <Plus size={14} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => onSelect(null)}
        aria-pressed={selectedFolderId === null}
        className={`rounded-md px-2 py-1.5 text-left text-sm ${
          selectedFolderId === null ? 'bg-surface-2 text-text-primary' : 'text-text-secondary hover:bg-surface-2'
        }`}
      >
        Todos os funis
      </button>

      {folders.map((folder) => (
        <div key={folder.id} className="group flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSelect(folder.id)}
            aria-pressed={selectedFolderId === folder.id}
            onDoubleClick={() => setRenaming(folder)}
            className={`flex flex-1 items-center gap-1.5 truncate rounded-md px-2 py-1.5 text-left text-sm ${
              selectedFolderId === folder.id
                ? 'bg-surface-2 text-text-primary'
                : 'text-text-secondary hover:bg-surface-2'
            }`}
          >
            <FolderIcon size={13} className="shrink-0" />
            <span className="truncate">{folder.name}</span>
          </button>
          <button
            type="button"
            aria-label={`Excluir pasta ${folder.name}`}
            onClick={() => handleDelete(folder)}
            className="rounded p-1 text-text-muted opacity-0 hover:text-[#f08484] group-hover:opacity-100"
          >
            <X size={12} />
          </button>
        </div>
      ))}

      <TextInputDialog
        open={createOpen}
        title="Criar pasta"
        label="Nome da pasta"
        submitLabel="Criar"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (name) => {
          await dashboardApi.createFolder(name);
          await reload();
        }}
      />

      <TextInputDialog
        open={renaming !== null}
        title="Renomear pasta"
        label="Nome da pasta"
        initialValue={renaming?.name}
        onClose={() => setRenaming(null)}
        onSubmit={async (name) => {
          if (!renaming) return;
          await dashboardApi.renameFolder(renaming.id, name);
          await reload();
        }}
      />
    </nav>
  );
}
